import { platformService } from '../services/platform.service';
import { ConfigurationName } from '../utils/constants';

/**
 * The tenant's TicketConfiguration, fetched once and shared by everything that reads it.
 *
 * Three places want fields off this one object: the rule editor asks whether the Time Entry
 * Start Time token should be offered, the Time Tracking tab asks about all three time entry
 * restrictions on every ticket it opens, and the settings page writes the whole thing. Before
 * this they each went to the server independently - the tab once per ticket, the rule page once
 * per editor - for a setting that changes about never.
 *
 * The promise is cached rather than the resolved value, so callers that start together share one
 * request instead of racing several. Same shape as pendingFeature in FeatureCache.
 *
 * setCachedTicketSettings is what makes an admin's own change take effect at once; the TTL is a
 * backstop for the changes this app cannot see - another admin, another tab, a direct OData
 * call. That split is deliberate and matches setCachedFeature, which the settings page calls
 * beside it for the licence flags.
 */
export interface TicketSettings {
  RestrictTimeEntryToCurrentDay: boolean;
  RestrictTimeEntryToQuarterHours: boolean;
  CaptureStartTime: boolean;
}

/**
 * What every reader gets when the setting cannot be read.
 *
 * All false is the unrestricted shape, which is the safe direction: the tab shows the date
 * picker and free-text minutes, and the server still enforces the current-day rule on its own -
 * so a failed read degrades the hints rather than the guarantee. The start time token is hidden,
 * which is what the setting being off would do anyway.
 */
const UNRESTRICTED: TicketSettings = {
  RestrictTimeEntryToCurrentDay: false,
  RestrictTimeEntryToQuarterHours: false,
  CaptureStartTime: false
};

const TTL = 60 * 60 * 1000; // 1 Hr, matching FeatureCache's invalidationPeriod

let entry: { value: Promise<TicketSettings>; at: number } | null = null;

/**
 * Records settings this app just saved, so the next read is served without a round trip and
 * without waiting out the TTL.
 *
 * Mirrors setCachedFeature, which the settings page calls two lines away for the same reason.
 */
export const setCachedTicketSettings = (settings: TicketSettings) => {
  entry = { value: Promise.resolve({ ...settings }), at: Date.now() };
};

/** Drops the cached value, so the next read goes to the server. */
export const clearTicketSettingsCache = () => {
  entry = null;
};

/**
 * The tenant's ticket settings. Never rejects - a failed read resolves to UNRESTRICTED, because
 * every caller here is choosing which control to draw and none of them should fail a page over
 * it.
 */
export const getTicketSettings = (): Promise<TicketSettings> => {
  const now = Date.now();
  if (entry && now - entry.at < TTL) return entry.value;

  const value = new platformService()
    .getSystemSetting(ConfigurationName.Ticket)
    .then((c: any) => ({
      RestrictTimeEntryToCurrentDay: c?.data?.RestrictTimeEntryToCurrentDay ?? false,
      RestrictTimeEntryToQuarterHours: c?.data?.RestrictTimeEntryToQuarterHours ?? false,
      CaptureStartTime: c?.data?.CaptureStartTime ?? false
    }))
    .catch(() => {
      // Not cached as a failure: every caller in the burst this exists to collapse would
      // otherwise be handed the same bad answer for an hour. Cleared so the next one retries.
      //
      // Only if it is still OUR entry. A save landing while this request was in flight has
      // already replaced it with the value the admin just chose, and that is newer than
      // anything this failed read could say.
      if (entry?.value === value) entry = null;
      return UNRESTRICTED;
    });

  entry = { value, at: now };
  return value;
};
